// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Reading boards straight out of a public git host.
 *
 * This is the one place the tool touches the network, and it is deliberately
 * one-directional: **GET only, no credentials, public repositories only.**
 * Nothing is ever uploaded. Every request omits cookies so that ambient
 * browser auth cannot leak to a self-hosted instance the user names.
 *
 * All three supported host families answer with
 * `Access-Control-Allow-Origin: *` on the endpoints used here, so no proxy and
 * no backend are involved.
 */

export type ProviderId = 'github' | 'gitlab' | 'gitea';

/** A repository the user pointed at. */
export interface RepoSource {
  provider: ProviderId;
  /** Origin, e.g. `https://github.com` or a self-hosted `https://git.example.org`. */
  origin: string;
  /** Owner or namespace. May contain slashes on GitLab (nested groups). */
  owner: string;
  repo: string;
  /** Human label, e.g. `github.com/owner/repo`. */
  label: string;
}

export interface RepoInfo {
  defaultBranch: string;
  /** Full name as the host reports it. */
  fullName: string;
}

export type RefKind = 'branch' | 'tag' | 'commit';

export interface GitRef {
  kind: RefKind;
  /** What to pass back to the API: a branch name, tag name or commit sha. */
  id: string;
  /** What to show the user. */
  label: string;
  /** Commit subject and date, for commits. */
  detail?: string;
}

/** Minimal response shape, so tests can stub the network. */
export interface HttpResponse {
  ok: boolean;
  status: number;
  statusText: string;
  headers: { get(name: string): string | null };
  text(): Promise<string>;
}

export type FetchLike = (url: string, init?: { headers?: Record<string, string> }) => Promise<HttpResponse>;

export interface Provider {
  readonly id: ProviderId;
  readonly source: RepoSource;
  /** Default branch and canonical name. The first call a caller makes. */
  info(): Promise<RepoInfo>;
  /** Every `.kicad_pcb` path in the tree at `ref`. */
  listBoardFiles(ref: string): Promise<string[]>;
  /** Branches and tags. */
  listRefs(): Promise<GitRef[]>;
  /** Recent commits touching `path`, newest first. */
  listCommits(ref: string, path: string): Promise<GitRef[]>;
  /** File contents at `ref`. */
  fetchFile(ref: string, path: string): Promise<string>;
  /** Where a human can see this file/ref in a browser. */
  webUrl(ref: string, path: string): string;
}

/** How many commits to offer per file. */
export const COMMIT_PAGE_SIZE = 30;
/** Cap on tree entries pulled while looking for board files. */
export const TREE_PAGE_SIZE = 1000;
